// Swipeable product-card image gallery: native scroll-snap track, prev/next commands, dots that follow the visible slide, and a one-time swipe hint.
class SCardGallery extends HTMLElement {
  #ac;
  #track;
  #dots = [];
  #index = 0;
  #observer;

  // Shared across galleries so only the first card a shopper sees per visit plays the hint.
  static hintKey = 'card-swipe-hint-shown';

  connectedCallback() {
    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;
    this.#dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command === '--next') this.#go(this.#index + 1);
      if (event.command === '--prev') this.#go(this.#index - 1);
    }, opts);

    this.#track.addEventListener('scroll', () => this.#sync(), { ...opts, passive: true });

    this.#enableMouseDrag(opts);

    if (this.hasAttribute('data-swipe-hint')) this.#watchForHint();
  }

  // Touch swipes use native scrolling; this adds the same drag for mouse and trackpad users (and the editor's mobile preview).
  #enableMouseDrag(opts) {
    const track = this.#track;
    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    let moved = false;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (track.scrollWidth <= track.clientWidth) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startScroll = track.scrollLeft;
    }, opts);

    track.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const delta = event.clientX - startX;
      if (!moved && Math.abs(delta) < 6) return;
      if (!moved) {
        moved = true;
        track.style.scrollSnapType = 'none';
        track.setPointerCapture?.(event.pointerId);
      }
      track.scrollLeft = startScroll - delta;
      event.preventDefault();
    }, opts);

    const end = () => {
      if (!dragging) return;
      dragging = false;
      if (!moved) return;
      track.style.scrollSnapType = '';
      const width = track.clientWidth || 1;
      this.#go(Math.round(Math.abs(track.scrollLeft) / width));
    };
    track.addEventListener('pointerup', end, opts);
    track.addEventListener('pointercancel', end, opts);

    // A drag must not also open the product link or zoom.
    track.addEventListener('click', (event) => {
      if (!moved) return;
      moved = false;
      event.preventDefault();
      event.stopPropagation();
    }, { ...opts, capture: true });

    track.addEventListener('dragstart', (event) => event.preventDefault(), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #watchForHint() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (sessionStorage.getItem(SCardGallery.hintKey)) return;
    this.#observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.#observer.disconnect();
      if (sessionStorage.getItem(SCardGallery.hintKey)) return;
      sessionStorage.setItem(SCardGallery.hintKey, '1');
      this.#playHint();
    }, { threshold: 0.6 });
    this.#observer.observe(this);
  }

  // Nudges the slides so the next image peeks in, twice, then settles back. Stops as soon as the shopper interacts.
  #playHint() {
    const rtl = getComputedStyle(this).direction === 'rtl';
    const shift = rtl ? '18%' : '-18%';
    const frames = [
      { transform: 'translateX(0)' },
      { transform: `translateX(${shift})`, offset: 0.25 },
      { transform: 'translateX(0)', offset: 0.5 },
      { transform: `translateX(${shift})`, offset: 0.75 },
      { transform: 'translateX(0)' },
    ];
    const animations = Array.from(this.#track.children).map((slide) =>
      slide.animate(frames, { duration: 1600, delay: 500, easing: 'ease-in-out' })
    );
    const stop = () => animations.forEach((animation) => animation.cancel());
    this.#track.addEventListener('pointerdown', stop, { once: true, signal: this.#ac.signal });
    this.#track.addEventListener('scroll', stop, { once: true, signal: this.#ac.signal });
  }

  get #count() {
    return this.#track.children.length;
  }

  #go(index) {
    const count = this.#count;
    if (!count) return;
    const next = (index + count) % count;
    const slide = this.#track.children[next];
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({ left: slide.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
  }

  #sync() {
    const width = this.#track.clientWidth || 1;
    const index = Math.round(Math.abs(this.#track.scrollLeft) / width);
    if (index === this.#index) return;
    this.#index = index;
    this.#dots.forEach((dot, i) => {
      dot.toggleAttribute('data-active', i === index);
    });
  }
}

if (!customElements.get('s-card-gallery')) customElements.define('s-card-gallery', SCardGallery);
